import { useEffect, useMemo, useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import type { Guest } from '@peles/shared';
import { EntityModal } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useGuests } from '../../hooks/useGuests';
import { useAddCampaignRecipients } from '../../hooks/useInvites';

/** Rows per page — enough to scan, short enough to stay a light modal. */
const PAGE_SIZE = 25;

export interface AddRecipientsModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  campaignId: string;
  /** Guests already on the campaign — hidden from the picker. */
  existingGuestIds: string[];
}

function guestName(guest: Guest): string {
  return [guest.firstName, guest.lastName].filter(Boolean).join(' ');
}

/**
 * Pick which guests join a campaign.
 *
 * "Add all" is the wrong tool for a second send, a reminder to one family, or
 * a list that is still being built — so this is a searchable checkbox list
 * with select-all over the current filter, rather than a dropdown: a wedding
 * list runs to hundreds of names and chips would be unreadable at that size.
 * Guests already on the campaign are omitted, so the list only ever shows what
 * can actually be added.
 */
export function AddRecipientsModal({
  open,
  onClose,
  eventId,
  campaignId,
  existingGuestIds,
}: AddRecipientsModalProps) {
  const { alert } = useDialog();
  const { data: guests = [], isLoading } = useGuests({ eventId });
  const addRecipients = useAddCampaignRecipients();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected([]);
    setPage(1);
  }, [open]);

  // Searching re-shuffles what is on each page, so start over at page 1.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const available = useMemo(() => {
    const existing = new Set(existingGuestIds);
    return guests.filter((guest) => !existing.has(guest.id));
  }, [guests, existingGuestIds]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return available;
    return available.filter((guest) =>
      [guestName(guest), guest.email ?? '', guest.familyGroup ?? '', guest.side ?? '']
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [available, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageGuests = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Select-all covers every guest the search matched, not just this page —
  // paging through 200 names to tick them all would defeat the point.
  const allShownSelected = filtered.length > 0 && filtered.every((g) => selected.includes(g.id));

  const toggle = (guestId: string) => {
    setSelected((current) =>
      current.includes(guestId) ? current.filter((id) => id !== guestId) : [...current, guestId],
    );
  };

  const toggleAllShown = () => {
    const shownIds = filtered.map((guest) => guest.id);
    setSelected((current) =>
      allShownSelected
        ? current.filter((id) => !shownIds.includes(id))
        : Array.from(new Set([...current, ...shownIds])),
    );
  };

  const handleAdd = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      const result = await addRecipients.mutateAsync({
        campaignId,
        input: { guestIds: selected },
      });
      await alert({
        message: `Added ${result.added} guest${result.added === 1 ? '' : 's'} to the campaign.`,
        variant: 'success',
      });
      onClose();
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to add guests',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Add Guests to Campaign"
      size="md"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-500">{selected.length} selected</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={saving || selected.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <UserPlus className="w-4 h-4" />
              {saving ? 'Adding…' : `Add ${selected.length || ''}`.trim()}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, family or side…"
            aria-label="Search guests"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {filtered.length > 0 && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={allShownSelected} onChange={toggleAllShown} />
            Select all {search.trim() ? 'matching' : ''} ({filtered.length})
          </label>
        )}

        <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {isLoading ? (
            <p className="p-4 text-sm text-gray-500" role="status">
              Loading guests…
            </p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              {available.length === 0
                ? 'Every guest on this event is already on the campaign.'
                : 'No guests match your search.'}
            </p>
          ) : (
            pageGuests.map((guest) => (
              <label
                key={guest.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(guest.id)}
                  onChange={() => toggle(guest.id)}
                  aria-label={`Select ${guestName(guest)}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-gray-900 truncate">{guestName(guest)}</span>
                  <span className="block text-xs text-gray-500 truncate">
                    {guest.email || 'No email'}
                    {guest.side ? ` · ${guest.side}` : ''}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>

        {pageCount > 1 && (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-gray-500">
              Page {currentPage} of {pageCount}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={currentPage === pageCount}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </EntityModal>
  );
}
